import {Component, inject, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject} from "rxjs";
import {ApiService, DocumentService, ToastService} from "../../services";
import {TranslateService} from "@ngx-translate/core";
import {Document, PageableDocuments} from "../../models";
import FileSaver from "file-saver";

@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrl: './documents.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentsComponent implements OnInit {
    private documentService = inject(DocumentService);
    private translateService = inject(TranslateService);
    private apiService = inject(ApiService);
    private toastService = inject(ToastService);

    public documents = new BehaviorSubject<Document[]>([]);
    public documentsLength = -1;
    public currentPageData: PageableDocuments;
    public currentPage = 0;
    private pageSize = 5;
    loadingSpinnerVisible = false;

    @Input() projectId: number;

    public tableConfig = {
        columns: [
            {header: this.translateService.instant(`PAGE_DOCUMENTS.table.column.fileName`)},
            {header: this.translateService.instant(`PAGE_DOCUMENTS.table.column.modifiedBy`)},
            {header: this.translateService.instant(`PAGE_DOCUMENTS.table.column.modifiedAt`)}
        ]
    };

    ngOnInit(): void {
        this.loadDocuments();
    }

    public setPage(pageNumber: number): void {
        this.currentPage = pageNumber - 1;
        this.loadDocuments();
    }

    public firstItem(): number {
        if (this.currentPageData?.page) {
            return (this.currentPageData.page.number * this.pageSize) + 1;
        } else {
            return 0;
        }
    }

    public lastItem(): number {
        if (this.currentPageData?.page) {
            return Math.min((this.currentPageData.page.number + 1) * this.pageSize, this.currentPageData.page.totalElements);
        } else {
            return 0;
        }
    }

    private loadDocuments() {
        this.loadingSpinnerVisible = true;
        this.documentService.getProjectDocuments(
            this.currentPage,
            this.pageSize,
            this.projectId
        ).then((pageableProjects) => {
            const content = pageableProjects.content;
            this.documentsLength = content?.length ?? 0;
            this.documents.next(content);
            this.currentPageData = pageableProjects;
        }).finally(() => {
            this.loadingSpinnerVisible = false;
        });
    }

    public onDocumentClick(document: Document) {
        this.apiService.getAttachment(`/documents/${document.uniqueId}`)
            .then((response: Blob) => {
                FileSaver.saveAs(response, document.originalFileName);
            })
            .catch(() => {
                this.toastService.openError(500, this.translateService.instant('ERRORS.pdf_download_failed'));
            });
    }
}
